/* eslint-disable camelcase */
import type { Currency } from '@backmarket/http-api'
import algoliasearch from 'algoliasearch'

import type { Product, Products } from '~/scopes/core/models/product'

import type { ProductsSearch } from '../models/products-search'

import type {
  AlgoliaClient,
  AlgoliaPayload,
  AlgoliaSearchParams,
} from './algolia-client'
import type { AlgoliaProductHit } from './algolia-product-hit'

export function mapSingleAlgoliaHitToProduct(
  algoliaHit: AlgoliaProductHit,
): Product {
  const {
    id,
    brand,
    category_3,
    currency,
    backbox_grade_label,
    backbox_grade_value,
    price_new,
    price,
    stockRaw,
    title_model,
    backmarketID,
    image1,
    link_grade_v2,
    warranty,
    reviewRating,
    sub_title_elements,
    listingID,
    model,
    color,
    merchant_id,
  } = algoliaHit

  const description = sub_title_elements.join(' - ')

  const specifications = {
    color,
  }

  return {
    id,
    title: title_model,
    description,
    listingId: `${listingID}`,
    model,
    brand,
    sellerId: merchant_id?.toString(),
    category: category_3,
    grade: {
      name: backbox_grade_label,
      value: backbox_grade_value,
    },
    productPageLink: link_grade_v2,
    priceNew: {
      amount: price_new.toString(),
      currency: currency as Currency,
    },
    price: {
      amount: price.toString(),
      currency: currency as Currency,
    },
    legacyId: `${backmarketID}`,
    reviewRating,
    availableStock: stockRaw,
    warrantyDuration: warranty,
    image: image1,
    specifications,
  }
}
export class AlgoliaProductsSearch
  implements ProductsSearch<AlgoliaSearchParams>
{
  #algoliaClient: AlgoliaClient

  constructor(algoliaClient: AlgoliaClient) {
    this.#algoliaClient = algoliaClient
  }

  async search(input: AlgoliaSearchParams) {
    const algoliaResponse = await this.#algoliaClient.search(input)
    const products = this.#mapAlgoliaResultsToProducts(algoliaResponse)

    return products
  }

  #mapAlgoliaResultsToProducts(algoliaPayload: AlgoliaPayload): Products {
    return algoliaPayload.results.flatMap((result) =>
      result.hits.map(mapSingleAlgoliaHitToProduct),
    )
  }
}

const algoliaClient: AlgoliaClient = {
  async search(params: AlgoliaSearchParams) {
    const client = algoliasearch(params.algoliaId, params.algoliaKey)

    /** need to do this type assertion because
      the return type is { results: Array<SearchResponse<TObject> | SearchForFacetValuesResponse>; }
      instead of { results: Array<SearchResponse<TObject> } * */

    return client.search(params.queries) as Promise<AlgoliaPayload>
  },
}

export const algoliaProductsSearch = new AlgoliaProductsSearch(algoliaClient)
